// Thin fetch wrapper for the studio REST API. Attaches a Firebase ID token when
// available (required by the deployed studio API; ignored by a local backend
// with STUDIO_AUTH_DISABLED) and surfaces the backend's `detail` message on
// errors.

import { API_BASE_URL } from "../config/env";
import { getIdToken } from "./auth";

export class ApiError extends Error {
  status: number;
  // The raw `detail` payload — an object for structured errors (e.g. the
  // talk-tracks 422 {message, checks}), where `message` alone would lose it.
  detail: unknown;
  constructor(message: string, status: number, detail?: unknown) {
    super(message);
    this.name = "ApiError";
    this.status = status;
    this.detail = detail;
  }
}

// Readable message for any FastAPI `detail`: a string, a {message} object, or
// a request-validation list of {loc, msg}.
function detailMessage(detail: unknown, fallback: string): string {
  if (typeof detail === "string") return detail;
  if (Array.isArray(detail)) {
    const parts = detail.map((d) => {
      if (!d || typeof d !== "object") return String(d);
      const { loc, msg } = d as { loc?: unknown[]; msg?: string };
      const where = Array.isArray(loc) ? loc.filter((l) => l !== "body").join(".") : "";
      return where ? `${where}: ${msg}` : String(msg);
    });
    return parts.join("; ") || fallback;
  }
  if (detail && typeof detail === "object" && "message" in detail) {
    return String((detail as { message: unknown }).message);
  }
  return fallback;
}

async function toApiError(res: Response): Promise<ApiError> {
  let detail: unknown = res.statusText;
  try {
    const data = await res.json();
    detail = data.detail || detail;
  } catch {
    /* non-JSON error body */
  }
  return new ApiError(detailMessage(detail, res.statusText), res.status, detail);
}

interface RequestOptions {
  method?: string;
  body?: unknown;
  // Skip JSON parsing and return the raw Response (e.g. for audio blobs).
  raw?: boolean;
}

async function authHeaders(): Promise<Record<string, string>> {
  const token = await getIdToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
}

export async function apiRequest<T>(path: string, options: RequestOptions = {}): Promise<T> {
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...(await authHeaders()),
  };

  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: options.method || "GET",
    headers,
    body: options.body !== undefined ? JSON.stringify(options.body) : undefined,
  });

  if (!res.ok) throw await toApiError(res);

  if (options.raw) return res as unknown as T;
  if (res.status === 204) return undefined as T;
  return (await res.json()) as T;
}

export async function apiBlob(path: string, options: RequestOptions = {}): Promise<Blob> {
  const res = await apiRequest<Response>(path, { ...options, raw: true });
  return res.blob();
}

// Multipart upload — no Content-Type header so the browser sets the boundary.
export async function apiUpload<T>(path: string, form: FormData): Promise<T> {
  const res = await fetch(`${API_BASE_URL}${path}`, {
    method: "POST",
    headers: await authHeaders(),
    body: form,
  });
  if (!res.ok) throw await toApiError(res);
  return (await res.json()) as T;
}
